import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type AutoStatus } from "../api/client";

/**
 * AUTO-mode state for the launch cockpit.
 *
 * <p>Polls {@code GET /api/auto/status} so a page reload rediscovers whether
 * AUTO is armed and whether it has tripped (the "Auto disarmed — cycle #N
 * closed −X€" card). Arming is a two-step confirmation: {@code requestOtp()}
 * emails a code, then {@code arm(otp)} sends the typed phrase {@code "ARM AUTO"}
 * plus the OTP. Disarming is one call (no confirmation).
 */

export const ARM_PHRASE = "ARM AUTO";

export class AutoArmError extends Error {
  readonly detail: string;
  constructor(detail: string) {
    super(detail);
    this.detail = detail;
  }
}

export interface AutoModeState {
  status: AutoStatus | null;
  pending: boolean;
  requestOtp: () => Promise<void>;
  arm: (phrase: string, otp: string) => Promise<AutoStatus>;
  disarm: () => Promise<AutoStatus>;
  /** « Reprendre maintenant » — lève toute pause (sans OTP) ; 409 si pas de pause. */
  resume: () => Promise<AutoStatus>;
}

export function useAutoMode(pollIntervalMs = 5_000): AutoModeState {
  const [status, setStatus] = useState<AutoStatus | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await api.autoStatus();
        if (!cancelled) setStatus(s);
      } catch {
        // Silent — system-state ERROR banner owns backend-reachability surfacing.
      }
    };
    void tick();
    const id = setInterval(tick, pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [pollIntervalMs]);

  const requestOtp = useCallback(async (): Promise<void> => {
    await api.requestAutoOtp();
  }, []);

  const arm = useCallback(async (phrase: string, otp: string): Promise<AutoStatus> => {
    setPending(true);
    try {
      const s = await api.armAuto({ phrase, otp });
      setStatus(s);
      return s;
    } catch (e) {
      if (e instanceof ApiError && typeof e.body === "object" && e.body !== null) {
        const detail = (e.body as { detail?: string }).detail ?? "Confirmation invalide.";
        throw new AutoArmError(detail);
      }
      throw e;
    } finally {
      setPending(false);
    }
  }, []);

  const disarm = useCallback(async (): Promise<AutoStatus> => {
    setPending(true);
    try {
      const s = await api.disarmAuto();
      setStatus(s);
      return s;
    } finally {
      setPending(false);
    }
  }, []);

  const resume = useCallback(async (): Promise<AutoStatus> => {
    setPending(true);
    try {
      const s = await api.resumeAuto();
      setStatus(s);
      return s;
    } finally {
      setPending(false);
    }
  }, []);

  return { status, pending, requestOtp, arm, disarm, resume };
}
